import { type FormEvent, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERMISSIONS } from '@lms/shared-types';
import { Loader, useToast } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listAllResource, listResource } from '@/lib/api';
import { groupShowBack } from '@/lib/group-navigation';
import { roleActionErrorToast } from '@/lib/user-feedback';

interface GroupMember {
  id: string;
  name: string;
  email?: string;
}

interface GroupAssignment {
  id: string;
  courseId?: string;
  title?: string;
  name?: string;
  assignedAt?: string;
}

interface GroupDetail {
  id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  isActive?: boolean;
  parent?: { id: string; name: string } | null;
  parentId?: string | null;
  members?: GroupMember[];
  assignments?: GroupAssignment[];
  children?: { id: string; name: string }[];
}

interface UserOption {
  id: string;
  name: string;
  email?: string;
  status?: string;
  trashed?: boolean;
}

interface CourseOption {
  id: string;
  name: string;
  title?: string;
  status?: string;
}

const LABEL = 'text-sm font-medium text-dash-on-surface-variant';
const FIELD =
  'w-full rounded-lg border border-dash-outline-variant bg-white px-4 py-2.5 text-base text-dash-on-surface outline-none transition-all focus:border-dash-secondary focus:ring-2 focus:ring-dash-secondary/20';

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const second = parts[1]?.[0] ?? parts[0]?.[1] ?? '';
  return (first + second).toUpperCase();
}

function formatAssignedAt(value?: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function GroupShowPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(PERMISSIONS.GRUPOS_ADMINISTRAR);

  const [userId, setUserId] = useState('');
  const [courseId, setCourseId] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['groups', id],
    queryFn: () => api<GroupDetail>(`/groups/${id}`),
    enabled: Boolean(id),
  });

  const { data: usersData } = useQuery({
    queryKey: ['users', 'group-members'],
    queryFn: () => listAllResource<UserOption>('/users'),
    enabled: canManage,
  });

  const { data: coursesData } = useQuery({
    queryKey: ['courses', 'group-assign'],
    queryFn: () => listResource<CourseOption>('/courses'),
    enabled: canManage,
  });

  const members = data?.members ?? [];
  const back = groupShowBack(data ?? {});
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const memberCount = members.length;

  const groupIsActive = data?.isActive !== false;

  const availableUsers = useMemo(
    () =>
      (usersData?.data ?? []).filter(
        (user) => !memberIds.has(user.id) && user.status === 'active' && !user.trashed,
      ),
    [usersData?.data, memberIds],
  );

  const assignedCourseIds = useMemo(
    () => new Set((data?.assignments ?? []).map((item) => item.courseId).filter(Boolean)),
    [data?.assignments],
  );

  const availableCourses = useMemo(
    () =>
      (coursesData?.data ?? []).filter((course) => {
        const title = course.title ?? course.name;
        return title && course.status === 'published' && !assignedCourseIds.has(course.id);
      }),
    [coursesData?.data, assignedCourseIds],
  );

  const invalidateGroup = () => queryClient.invalidateQueries({ queryKey: ['groups', id] });

  useEffect(() => {
    if (!error) return;
    toast(roleActionErrorToast(error, 'No se pudo cargar el grupo'));
  }, [error, toast]);

  const addMemberMutation = useMutation({
    mutationFn: (selectedUserId: string) =>
      api(`/groups/${id}/members`, { method: 'POST', body: { userId: selectedUserId } }),
    onSuccess: async () => {
      setUserId('');
      await invalidateGroup();
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => toast(roleActionErrorToast(err, 'No se pudo agregar el miembro')),
  });

  const removeMemberMutation = useMutation({
    mutationFn: (selectedUserId: string) =>
      api(`/groups/${id}/members/${selectedUserId}`, { method: 'DELETE' }),
    onSuccess: async () => {
      await invalidateGroup();
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => toast(roleActionErrorToast(err, 'No se pudo quitar el miembro')),
  });

  const assignCourseMutation = useMutation({
    mutationFn: (selectedCourseId: string) =>
      api(`/groups/${id}/assign`, { method: 'POST', body: { courseId: selectedCourseId } }),
    onSuccess: async () => {
      setCourseId('');
      await invalidateGroup();
      await queryClient.invalidateQueries({ queryKey: ['groups'] });
    },
    onError: (err) => toast(roleActionErrorToast(err, 'No se pudo asignar el curso')),
  });

  function handleAddMember(e: FormEvent) {
    e.preventDefault();
    if (!userId) return;
    addMemberMutation.mutate(userId);
  }

  function handleAssignCourse(e: FormEvent) {
    e.preventDefault();
    if (!courseId) return;
    assignCourseMutation.mutate(courseId);
  }

  return (
    <div className="lms-site-admin-hub-page font-sans">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex items-start gap-4">
          <HubBackLink to={back.to} label={back.label} />
          <div className="min-w-0">
            <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">
              {data?.name ?? 'Grupo'}
            </h1>
            {data?.code && (
              <p className="mt-1 text-sm text-dash-on-surface-variant">Código {data.code}</p>
            )}
            {data?.isActive === false && (
              <span className="mt-2 inline-flex rounded-full bg-error-100 px-2.5 py-0.5 text-xs font-semibold text-error-700">
                Inactivo
              </span>
            )}
          </div>
        </div>
        {canManage && (
          <Link
            to={`/grupos/${id}/edit`}
            className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-dash-outline-variant bg-white px-6 py-2.5 text-sm font-medium text-dash-primary shadow-sm transition-all hover:bg-secondary-50 sm:mt-1"
          >
            <span className="material-symbols-outlined text-xl" aria-hidden="true">
              edit
            </span>
            Editar
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
            <section className="lms-credentials-glass-panel flex min-h-[28rem] flex-col overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm lg:col-span-7">
              <div className="flex items-center justify-between border-b border-dash-outline-variant bg-secondary-50/50 px-6 py-5">
                <h2 className="text-lg font-semibold text-dash-on-surface">Miembros</h2>
                <span className="rounded-full bg-brand-100 px-3 py-1 text-xs font-semibold text-brand-900">
                  {memberCount} {memberCount === 1 ? 'Miembro' : 'Miembros'}
                </span>
              </div>

              <div className="flex flex-grow flex-col p-6">
                {canManage && groupIsActive && (
                  <form onSubmit={handleAddMember} className="flex flex-col gap-3 md:flex-row">
                    <div className="relative min-w-0 flex-1">
                      <span
                        className="material-symbols-outlined pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-dash-on-surface-variant"
                        aria-hidden="true"
                      >
                        search
                      </span>
                      <select
                        value={userId}
                        onChange={(e) => setUserId(e.target.value)}
                        required
                        className={`${FIELD} cursor-pointer appearance-none pl-10 pr-10`}
                      >
                        <option value="" disabled>
                          Buscar y agregar usuarios...
                        </option>
                        {availableUsers.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name} — {user.email}
                          </option>
                        ))}
                      </select>
                    </div>
                    <button
                      type="submit"
                      disabled={addMemberMutation.isPending || !userId}
                      className="inline-flex shrink-0 items-center justify-center rounded-lg bg-dash-primary px-8 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                    >
                      {addMemberMutation.isPending ? 'Agregando…' : 'Agregar'}
                    </button>
                  </form>
                )}
                {canManage && !groupIsActive && (
                  <p className="text-sm text-dash-on-surface-variant">
                    Este grupo está inactivo. No se pueden agregar ni quitar miembros.
                  </p>
                )}

                <div className="mt-6 flex-grow space-y-2 overflow-y-auto pr-1">
                  {memberCount === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-dash-outline-variant bg-secondary-50/30 px-6 py-12 text-center">
                      <span
                        className="material-symbols-outlined mb-3 text-5xl font-extralight text-dash-on-surface-variant/60"
                        aria-hidden="true"
                      >
                        group_off
                      </span>
                      <h3 className="text-sm font-medium text-dash-on-surface">Sin miembros</h3>
                      <p className="mt-1 max-w-xs text-sm text-dash-on-surface-variant">
                        Agrega usuarios a este grupo.
                      </p>
                    </div>
                  ) : (
                    members.map((member) => (
                      <div
                        key={member.id}
                        className="group flex items-center justify-between rounded-xl border border-transparent p-4 transition-all hover:border-dash-outline-variant hover:bg-white"
                      >
                        <div className="flex min-w-0 items-center gap-4">
                          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-secondary-200 text-sm font-bold text-secondary-900">
                            {initialsFor(member.name)}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium text-dash-on-surface">{member.name}</p>
                            {member.email && (
                              <p className="truncate text-xs text-dash-on-surface-variant">{member.email}</p>
                            )}
                          </div>
                        </div>
                        {canManage && groupIsActive && (
                          <button
                            type="button"
                            onClick={() => removeMemberMutation.mutate(member.id)}
                            disabled={removeMemberMutation.isPending}
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold text-error-600 opacity-100 transition-colors hover:bg-error-50 sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            <span className="material-symbols-outlined text-lg" aria-hidden="true">
                              person_remove
                            </span>
                            Quitar
                          </button>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </section>

            <section className="flex flex-col gap-6 lg:col-span-5">
              <div className="lms-credentials-glass-panel flex flex-grow flex-col overflow-hidden rounded-xl border border-dash-outline-variant shadow-sm">
                <div className="border-b border-dash-outline-variant bg-secondary-50/50 px-6 py-5">
                  <h2 className="text-lg font-semibold text-dash-on-surface">Asignaciones</h2>
                </div>
                <div className="flex flex-grow flex-col p-6">
                  {canManage && (
                    <form onSubmit={handleAssignCourse} className="mb-8 space-y-3">
                      <label htmlFor="assignable_course_id" className={LABEL}>
                        Asignar nuevo curso
                      </label>
                      <select
                        id="assignable_course_id"
                        value={courseId}
                        onChange={(e) => setCourseId(e.target.value)}
                        required
                        className={`${FIELD} cursor-pointer appearance-none`}
                      >
                        <option value="" disabled>
                          Seleccionar curso...
                        </option>
                        {availableCourses.map((course) => (
                          <option key={course.id} value={course.id}>
                            {course.title ?? course.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="submit"
                        disabled={assignCourseMutation.isPending || !courseId}
                        className="inline-flex w-full items-center justify-center rounded-lg bg-dash-secondary px-6 py-2.5 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                      >
                        {assignCourseMutation.isPending ? 'Asignando…' : 'Asignar'}
                      </button>
                    </form>
                  )}

                  {(data?.assignments ?? []).length === 0 ? (
                    <div className="flex flex-grow flex-col items-center justify-center rounded-2xl border-2 border-dashed border-dash-outline-variant bg-secondary-50/30 p-8 text-center">
                      <span
                        className="material-symbols-outlined mb-4 text-6xl font-extralight text-dash-on-surface-variant/50"
                        aria-hidden="true"
                      >
                        auto_stories
                      </span>
                      <h3 className="text-sm font-medium text-dash-on-surface">Sin asignaciones</h3>
                      <p className="mt-2 max-w-[240px] text-sm text-dash-on-surface-variant">
                        Asigna cursos a todo el grupo para que aparezcan aquí.
                      </p>
                    </div>
                  ) : (
                    <ul className="space-y-3">
                      {(data?.assignments ?? []).map((item) => (
                        <li key={item.id} className="rounded-xl border border-dash-outline-variant bg-white p-4">
                          <p className="font-medium text-dash-on-surface">{item.title ?? item.name}</p>
                          <p className="mt-1 text-xs text-dash-on-surface-variant">
                            Asignado {formatAssignedAt(item.assignedAt)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </section>
          </div>

          {(data?.children ?? []).length > 0 && (
            <div className="lms-credentials-glass-panel mt-6 rounded-xl border border-dash-outline-variant p-6 shadow-sm">
              <h2 className="mb-4 text-xs font-semibold uppercase tracking-widest text-dash-primary">Subgrupos</h2>
              <div className="flex flex-wrap gap-2">
                {(data?.children ?? []).map((child) => (
                  <Link
                    key={child.id}
                    to={`/grupos/${child.id}`}
                    className="inline-flex items-center gap-2 rounded-lg border border-dash-outline-variant bg-white px-4 py-2 text-sm font-medium text-dash-primary transition-colors hover:bg-secondary-50"
                  >
                    <span className="material-symbols-outlined text-lg text-dash-on-surface-variant" aria-hidden="true">
                      subheader
                    </span>
                    {child.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
